/**
 * Markdown → HTML.
 *
 * Used when the input mode is "Markdown", so the same tool can go the other
 * direction: paste Markdown, get clean HTML (or clean Markdown back out).
 *
 * This is a pragmatic subset of GitHub-flavoured Markdown — headings, lists
 * (nested), fenced and indented code, blockquotes, rules, pipe tables, and the
 * usual inline markers. It is not a full CommonMark implementation, and it does
 * not try to be: the input here is normal prose written by a person or an AI,
 * not a torture-test document.
 */

const HR_RE = /^ {0,3}([-*_])[ \t]*(\1[ \t]*){2,}$/;
const HEADING_RE = /^ {0,3}(#{1,6})[ \t]+(.*?)[ \t]*#*[ \t]*$/;
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})[ \t]*([\w+#-]*)[ \t]*$/;
const BULLET_RE = /^(\s*)([-*+])[ \t]+(.*)$/;
const ORDERED_RE = /^(\s*)(\d{1,9})[.)][ \t]+(.*)$/;
const QUOTE_RE = /^ {0,3}>[ \t]?(.*)$/;
const TABLE_DIVIDER_RE = /^[ \t]*\|?[ \t]*:?-{1,}:?[ \t]*(\|[ \t]*:?-{1,}:?[ \t]*)*\|?[ \t]*$/;

/**
 * Placeholder markers used while parsing inline text. They live in the Unicode
 * private-use area, so they can never collide with real pasted content.
 */
const MARK_CODE_OPEN = '\uE000';
const MARK_CODE_CLOSE = '\uE001';
const MARK_TAG_OPEN = '\uE002';
const MARK_TAG_CLOSE = '\uE003';

export function markdownToHtml(md: string): string {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  return renderBlocks(lines).trim();
}

// ── Block level ──────────────────────────────────────────────────────────────

function renderBlocks(lines: string[]): string {
  let out = '';
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // Fenced code block
    const fence = FENCE_RE.exec(line);
    if (fence) {
      const marker = fence[1][0];
      const lang = fence[2];
      const body: string[] = [];
      i++;
      const closer = new RegExp(`^ {0,3}\\${marker}{${fence[1].length},}[ \\t]*$`);
      while (i < lines.length && !closer.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      i++; // consume the closing fence
      const cls = lang ? ` class="language-${escapeAttr(lang)}"` : '';
      out += `<pre><code${cls}>${escapeHtml(body.join('\n'))}</code></pre>\n`;
      continue;
    }

    if (HR_RE.test(line)) { out += '<hr>\n'; i++; continue; }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = heading[1].length;
      out += `<h${level}>${renderInline(heading[2])}</h${level}>\n`;
      i++;
      continue;
    }

    // Setext heading: text on one line, ==== or ---- on the next.
    if (i + 1 < lines.length && /^ {0,3}(=+|-+)[ \t]*$/.test(lines[i + 1])
      && line.trim() && !BULLET_RE.test(line) && !HR_RE.test(lines[i + 1])) {
      const level = lines[i + 1].trim().startsWith('=') ? 1 : 2;
      out += `<h${level}>${renderInline(line.trim())}</h${level}>\n`;
      i += 2;
      continue;
    }

    // Blockquote — collect the run, strip one level of `>`, recurse.
    if (QUOTE_RE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && (QUOTE_RE.test(lines[i]) || (lines[i].trim() && inner.length))) {
        const m = QUOTE_RE.exec(lines[i]);
        inner.push(m ? m[1] : lines[i]);
        i++;
      }
      out += `<blockquote>\n${renderBlocks(inner)}</blockquote>\n`;
      continue;
    }

    // Pipe table — needs the `---|---` divider on the second line.
    if (line.includes('|') && i + 1 < lines.length
      && TABLE_DIVIDER_RE.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const header = line;
      const rows: string[] = [];
      i += 2;
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) {
        rows.push(lines[i]);
        i++;
      }
      out += renderTable(header, rows);
      continue;
    }

    if (BULLET_RE.test(line) || ORDERED_RE.test(line)) {
      const [html, next] = renderList(lines, i);
      out += html;
      i = next;
      continue;
    }

    // Indented code block (4 spaces).
    if (/^ {4,}\S/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && (/^ {4}/.test(lines[i]) || !lines[i].trim())) {
        body.push(lines[i].replace(/^ {4}/, ''));
        i++;
      }
      out += `<pre><code>${escapeHtml(body.join('\n').replace(/\n+$/, ''))}</code></pre>\n`;
      continue;
    }

    // Raw block-level HTML — pass it straight through.
    if (/^ {0,3}<\/?[a-zA-Z][\w-]*/.test(line)
      && !/^ {0,3}<\/?(a|b|i|em|strong|code|span|img|br|small|sub|sup|del|s|mark)\b/i.test(line)) {
      while (i < lines.length && lines[i].trim()) { out += lines[i] + '\n'; i++; }
      continue;
    }

    // Paragraph — run until a blank line or the start of another block.
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i], para.length > 0)) {
      para.push(lines[i].trim());
      i++;
    }
    if (para.length) out += `<p>${renderInline(para.join('\n'))}</p>\n`;
    else i++; // safety: never spin on a line we cannot classify
  }

  return out;
}

/** Would this line begin a new block, ending the paragraph above it? */
function startsBlock(line: string, insideParagraph: boolean): boolean {
  if (!insideParagraph) return false;
  return (
    HR_RE.test(line) ||
    HEADING_RE.test(line) ||
    FENCE_RE.test(line) ||
    QUOTE_RE.test(line) ||
    BULLET_RE.test(line) ||
    ORDERED_RE.test(line)
  );
}

/**
 * Lists, including nesting. An item owns every following line indented past its
 * own marker; those lines are parsed recursively, so nested lists, code blocks,
 * and extra paragraphs inside an item all work.
 */
function renderList(lines: string[], start: number): [string, number] {
  const first = BULLET_RE.exec(lines[start]) ?? ORDERED_RE.exec(lines[start]);
  if (!first) return ['', start + 1];

  const baseIndent = indentOf(lines[start]);
  const ordered = !BULLET_RE.test(lines[start]);
  const startNum = ordered ? Number(first[2]) : 1;

  const items: string[][] = [];
  let i = start;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      // A blank line only continues the list if indented content follows.
      const next = lines[i + 1];
      if (!next || !next.trim()) break;
      if (indentOf(next) <= baseIndent && !isItemStart(next, baseIndent)) break;
      if (items.length) items[items.length - 1].push('');
      i++;
      continue;
    }

    const match = BULLET_RE.exec(line) ?? ORDERED_RE.exec(line);
    const indent = indentOf(line);

    if (match && indent === baseIndent) {
      // A different marker style at the same level starts a separate list.
      const isOrdered = !BULLET_RE.test(line);
      if (items.length && isOrdered !== ordered) break;
      items.push([match[3]]);
      i++;
      continue;
    }

    if (indent > baseIndent && items.length) {
      // Strip this list's indent so nested content parses from column zero.
      items[items.length - 1].push(line.slice(Math.min(indent, baseIndent + 2)));
      i++;
      continue;
    }

    break;
  }

  const tag = ordered ? 'ol' : 'ul';
  const startAttr = ordered && startNum !== 1 ? ` start="${startNum}"` : '';
  let html = `<${tag}${startAttr}>\n`;
  for (const item of items) html += `<li>${renderItem(item)}</li>\n`;
  html += `</${tag}>\n`;

  return [html, i];
}

function isItemStart(line: string, baseIndent: number): boolean {
  const m = BULLET_RE.exec(line) ?? ORDERED_RE.exec(line);
  return !!m && indentOf(line) >= baseIndent;
}

function indentOf(line: string): number {
  return /^[ \t]*/.exec(line)![0].replace(/\t/g, '    ').length;
}

/**
 * An item's content. A simple item stays inline (`<li>text</li>`); anything
 * richer — a nested list, a code fence — goes through the full block parser.
 */
function renderItem(lines: string[]): string {
  const trimmed = [...lines];
  while (trimmed.length && !trimmed[trimmed.length - 1].trim()) trimmed.pop();
  if (!trimmed.length) return '';

  const rest = trimmed.slice(1);
  const restIsPlain = rest.every(l =>
    l.trim() !== '' && !BULLET_RE.test(l) && !ORDERED_RE.test(l) && !FENCE_RE.test(l) && !QUOTE_RE.test(l));

  if (restIsPlain) return renderInline(trimmed.join('\n').trim());

  // Lead-in line stays inline, the rest becomes real blocks.
  return `${renderInline(trimmed[0].trim())}\n${renderBlocks(rest)}`.trim();
}

function renderTable(headerLine: string, rows: string[]): string {
  const cells = (line: string): string[] =>
    line.replace(/^[ \t]*\|/, '').replace(/\|[ \t]*$/, '').split('|').map(c => c.trim());

  let html = '<table>\n<thead>\n<tr>';
  for (const cell of cells(headerLine)) html += `<th>${renderInline(cell)}</th>`;
  html += '</tr>\n</thead>\n<tbody>\n';
  for (const row of rows) {
    html += '<tr>';
    for (const cell of cells(row)) html += `<td>${renderInline(cell)}</td>`;
    html += '</tr>\n';
  }
  return html + '</tbody>\n</table>\n';
}

// ── Inline level ─────────────────────────────────────────────────────────────

/**
 * Code spans are pulled out first and swapped back in at the end, so a `*` or
 * `_` inside backticks is never mistaken for emphasis.
 */
function renderInline(text: string): string {
  const codes: string[] = [];
  let work = text.replace(/(`+)([\s\S]*?)\1/g, (_m, _fence: string, code: string) => {
    codes.push(`<code>${escapeHtml(code.replace(/^ (.*) $/, '$1'))}</code>`);
    return `${MARK_CODE_OPEN}${codes.length - 1}${MARK_CODE_CLOSE}`;
  });

  work = escapeHtmlKeepingTags(work);

  work = work
    // Images before links — the syntax only differs by the leading `!`.
    .replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g,
      (_m, alt: string, src: string, title?: string) =>
        `<img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}"${title ? ` title="${escapeAttr(title)}"` : ''}>`)
    .replace(/\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g,
      (_m, label: string, href: string, title?: string) =>
        `<a href="${escapeAttr(href)}"${title ? ` title="${escapeAttr(title)}"` : ''}>${label}</a>`)
    // Bare <https://…> autolinks (the angle brackets are escaped by this point).
    .replace(/&lt;(https?:\/\/[^\s>]+)&gt;/g, '<a href="$1">$1</a>')
    .replace(/~~([\s\S]+?)~~/g, '<del>$1</del>')
    .replace(/(\*\*\*|___)(?=\S)([\s\S]+?)(?<=\S)\1/g, '<strong><em>$2</em></strong>')
    .replace(/(\*\*|__)(?=\S)([\s\S]+?)(?<=\S)\1/g, '<strong>$2</strong>')
    .replace(/(?<![\w*])\*(?=\S)([\s\S]+?)(?<=\S)\*(?![\w*])/g, '<em>$1</em>')
    .replace(/(?<![\w_])_(?=\S)([\s\S]+?)(?<=\S)_(?![\w_])/g, '<em>$1</em>')
    // Two trailing spaces = hard line break; a lone newline is just a space.
    .replace(/ {2,}\n/g, '<br>\n')
    .replace(/\n/g, ' ');

  // Un-escape backslash-escaped punctuation now that parsing is done.
  work = work.replace(/\\([\\`*_{}[\]()#+\-.!>~|])/g, '$1');

  return work.replace(
    new RegExp(`${MARK_CODE_OPEN}(\\d+)${MARK_CODE_CLOSE}`, 'g'),
    (_m, n: string) => codes[Number(n)],
  );
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Escape everything except inline HTML the author wrote on purpose (`<br>`,
 * `<sup>`, a raw `<a href>`), which Markdown allows to pass through.
 */
function escapeHtmlKeepingTags(text: string): string {
  const tags: string[] = [];
  const held = text.replace(/<\/?[a-zA-Z][\w-]*(?:\s[^<>]*)?\/?>/g, (m) => {
    tags.push(m);
    return `${MARK_TAG_OPEN}${tags.length - 1}${MARK_TAG_CLOSE}`;
  });
  return escapeHtml(held).replace(
    new RegExp(`${MARK_TAG_OPEN}(\\d+)${MARK_TAG_CLOSE}`, 'g'),
    (_m, n: string) => tags[Number(n)],
  );
}

function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/"/g, '&quot;');
}
